import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import * as Sentry from "@sentry/react";

import {
  acceptAccountRequestNotification,
  acceptConsentLink,
  registerFalRequest,
  validateConsentLink,
} from "../connectors/connectors";
import {
  ConsentComitente,
  ConsentInvalidLink,
  ConsentLinkReason,
  ConsentUnavailable,
  ConsentVariant,
  PartnerConsentFlow,
  UUID_PATTERN,
  variantByKind,
} from "../utils/partnerConsent";
import { privateParams } from "../utils/privateParams";
import { isDev } from "../app/utils";

/** Screen the flow is currently on. */
export type ConsentScreen =
  | "LOADING"
  | "WELCOME"
  | "PRECONFIRMATION"
  | "SUCCESS"
  | "ALREADY_ACCEPTED"
  | "COMPLETED"
  | "INVALID_LINK"
  | "ERROR";

/** Each way a link can be unusable maps to its own screen. */
const screenByReason: Record<ConsentLinkReason, ConsentScreen> = {
  not_found: "INVALID_LINK",
  // Dead, aged-out, and superseded all share a screen; Sentry still gets the reason.
  expired: "INVALID_LINK",
  superseded: "INVALID_LINK",
  already_used: "ALREADY_ACCEPTED",
  already_completed: "COMPLETED",
  // Our bug, not a dead link: the generic error is the one that offers a retry.
  invalid_payload: "ERROR",
  // The link survives it, so it gets the retry as well.
  comitentes_unavailable: "ERROR",
};

/**
 * Read from `import.meta.env` and not the `isDev` helper, which carries the
 * same value: Vite folds this to a literal, so a production build drops the
 * mock screens instead of relying on Rollup to propagate the constant.
 */
const MOCKS_ENABLED = import.meta.env.VITE_ENVIRONMENT === "dev";

/** Walk the screens with no partner link: `?mock=welcome`, `?mock=success`… */
const mockScreens: Record<string, ConsentScreen> = {
  loading: "LOADING",
  welcome: "WELCOME",
  preconfirm: "PRECONFIRMATION",
  success: "SUCCESS",
  accepted: "ALREADY_ACCEPTED",
  completed: "COMPLETED",
  invalid: "INVALID_LINK",
  error: "ERROR",
};

/** Stands in for a validated link. No DNI: no screen shows one. */
const mockLink = {
  cuit: "20264920537",
  first: "JUAN CARLOS",
  last: "PÉREZ",
  holder: "Esquina 3 S.A",
  comitente: "20664",
  comitentes: [
    { numero: "600234", nombre: "Juan Perez" },
    { numero: "123123", nombre: "Romina García" },
    { numero: "123124", nombre: "Juan Perez" },
  ] as ConsentComitente[],
};

/** The name service answers in capitals, which reads as shouting in a greeting. */
const toDisplayCase = (value: string | null) =>
  (value ?? "")
    .toLowerCase()
    .replace(
      /(^|[\s'-])(\p{L})/gu,
      (_, sep, letter) => sep + letter.toUpperCase(),
    );

/** Query params a `params` link carries in place of a token. */
const linkParams = {
  comitente: "comitente",
  holder: "razon_social",
  uuid: "uuid",
  token: "token",
};

/**
 * Midgard reports a `code` where the partner Lambdas send a `reason`. An
 * unknown uuid also comes back as INVALID_TOKEN, so the two can't be told apart.
 * TODO(backend): a code for an expired request.
 */
const reasonByCode: Record<string, ConsentLinkReason> = {
  INVALID_TOKEN: "not_found",
  ALREADY_ACCEPTED: "already_used",
};

/** null for anything with no reason (network, 500, CORS) → generic error. */
const reasonFromError = (error: unknown): ConsentLinkReason | null => {
  if (!axios.isAxiosError(error)) return null;

  const data = error.response?.data as
    | (Partial<ConsentInvalidLink & ConsentUnavailable> & { code?: string })
    | undefined;
  const reason = data?.reason ?? data?.error ?? reasonByCode[data?.code ?? ""];
  return reason && reason in screenByReason ? reason : null;
};

const REGISTER_ATTEMPTS = 3;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Drives a partner's consent flow: which screen to render and the data it shows.
 * On token links the token is the only credential; nothing is decrypted here.
 */
const usePartnerConsent = (flow: PartnerConsentFlow) => {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();

  const mockScreen = MOCKS_ENABLED
    ? mockScreens[searchParams.get("mock") ?? ""]
    : undefined;

  const fromParams = flow.link === "params";
  const readParam = (key: string, mock = "") =>
    (fromParams && searchParams.get(key)) || (mockScreen ? mock : "");

  const holder = readParam(linkParams.holder, mockLink.holder);
  const comitente = readParam(linkParams.comitente, mockLink.comitente);
  const requestUuid = readParam(linkParams.uuid);
  const requestToken = readParam(linkParams.token);

  // Token links learn it from the response `kind`; `?variant=` is mocks only.
  const forcedVariant = (
    mockScreen ? searchParams.get("variant")?.toUpperCase() : undefined
  ) as ConsentVariant;
  const [variant, setVariant] = useState<ConsentVariant>(
    flow.variants.includes(forcedVariant) ? forcedVariant : flow.variants[0],
  );

  const [screen, setScreen] = useState<ConsentScreen>(mockScreen ?? "LOADING");
  const [cuit, setCuit] = useState(mockScreen ? mockLink.cuit : "");
  const [comitentes, setComitentes] = useState<ConsentComitente[]>(
    mockScreen ? mockLink.comitentes : [],
  );
  // The mocked preconfirmation has no welcome to pick on, so it gets the first.
  const [selectedComitente, setSelectedComitente] = useState(
    mockScreen === "PRECONFIRMATION" ? mockLink.comitentes[0].numero : "",
  );
  const [firstName, setFirstName] = useState(mockScreen ? mockLink.first : "");
  const [lastName, setLastName] = useState(mockScreen ? mockLink.last : "");
  const [expiresInSeconds, setExpiresInSeconds] = useState<number | null>(null);
  // Keyed by checkbox name; which names exist is the welcome screen's business.
  const [accepted, setAccepted] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Not keyed on `variant`: validate sets it, and a new `failWith` would re-run
  // validate's effect and fetch the link a second time.
  const failWith = useCallback(
    (error: unknown, action: string, variant?: ConsentVariant) => {
      const reason = reasonFromError(error);

      Sentry.captureException(error, {
        extra: { reason, action, variant },
        tags: { partnership: flow.key, reason: reason ?? "unknown" },
      });

      // Several failures share a screen: this is what tells a 410 from a 500.
      if (isDev) console.warn(`[${flow.key}] ${action} failed:`, reason, error);

      setScreen(reason ? screenByReason[reason] : "ERROR");
    },
    [flow.key],
  );

  const validate = useCallback(() => {
    // Nothing to ask the backend before signing: there is no lookup endpoint,
    // so a bad token only shows when accepting.
    if (fromParams) {
      const isValid = UUID_PATTERN.test(requestUuid) && Boolean(requestToken);
      setScreen(isValid ? "WELCOME" : "INVALID_LINK");
      return () => {};
    }

    if (!token) {
      setScreen("INVALID_LINK");
      return () => {};
    }

    let cancelled = false;
    setScreen("LOADING");

    validateConsentLink({ baseUrl: flow.baseUrl, token })
      .then(({ data }) => {
        if (cancelled) return;

        // Unknown kind: the contract moved on, so no screen here is the right one.
        // A single-variant flow has nothing for the kind to decide.
        const linkVariant =
          flow.variants.length === 1
            ? flow.variants[0]
            : variantByKind[data.kind];
        if (!linkVariant) {
          Sentry.captureException(
            new Error(`Unknown consent link kind: ${data.kind}`),
            {
              extra: { action: "validate", kind: data.kind },
              tags: { partnership: flow.key, reason: "unknown_kind" },
            },
          );
          setScreen("ERROR");
          return;
        }

        // Nothing to link: letting it through burns the link on an empty
        // vinculación. `null` (service down) and `[]` (titular of none) share
        // the screen and stay apart in Sentry.
        if (linkVariant === "VINCULACION" && !data.comitentes?.length) {
          const isUnavailable = !data.comitentes;

          Sentry.captureException(
            new Error(
              isUnavailable
                ? "Consent link returned null comitentes"
                : "Consent link returned an empty comitentes list",
            ),
            {
              extra: { action: "validate", kind: data.kind },
              tags: {
                partnership: flow.key,
                reason: isUnavailable
                  ? "comitentes_unavailable"
                  : "comitentes_empty",
              },
            },
          );
          setScreen("ERROR");
          return;
        }

        setVariant(linkVariant);
        setCuit(data.cuit ?? "");
        setComitentes(data.comitentes ?? []);
        // One comitente is no choice: the selector only shows for several.
        setSelectedComitente(
          data.comitentes?.length === 1 ? data.comitentes[0].numero : "",
        );
        // Nullable: the lookup may fail without taking the screen down.
        setFirstName(data.first_name ?? "");
        setLastName(data.last_name ?? "");
        setExpiresInSeconds(data.expires_in_seconds);
        setScreen("WELCOME");
      })
      .catch((error) => {
        if (cancelled) return;
        failWith(error, "validate");
      });

    return () => {
      cancelled = true;
    };
  }, [
    token,
    fromParams,
    requestUuid,
    requestToken,
    flow.baseUrl,
    flow.key,
    flow.variants,
    failWith,
  ]);

  useEffect(() => {
    if (mockScreen) return;
    return validate();
  }, [mockScreen, validate]);

  // A link lives thirty minutes and the page can sit open longer. Counted off
  // the backend's duration, not `expires_at`, which a wrong device clock breaks.
  useEffect(() => {
    if (expiresInSeconds === null) return;

    const timer = window.setTimeout(
      () =>
        // Once signed the link is burned: success must not turn into an error.
        setScreen((current) =>
          current === "WELCOME" || current === "PRECONFIRMATION"
            ? "INVALID_LINK"
            : current,
        ),
      Math.max(0, expiresInSeconds) * 1000,
    );

    return () => window.clearTimeout(timer);
  }, [expiresInSeconds]);

  // Without this row the fund choice from the welcome email is rejected, so a
  // failure is retried but never undoes the accepted request on screen.
  const registerRequest = async () => {
    if (!flow.registersFalRequest) return;

    let lastError: unknown;
    for (let attempt = 1; attempt <= REGISTER_ATTEMPTS; attempt++) {
      try {
        const { data } = await registerFalRequest({
          uuid: requestUuid,
          razonSocial: holder,
          comitente,
          mail: privateParams.mail ?? "",
        });
        if (data.ok) return;
        lastError = new Error(`${data.codigo}: ${data.mensaje}`);
        if (data.codigo === "DATOS_INVALIDOS") break;
      } catch (error) {
        lastError = error;
      }
      if (attempt < REGISTER_ATTEMPTS) await wait(attempt * 1000);
    }

    Sentry.captureException(lastError, {
      extra: { action: "register" },
      tags: { partnership: flow.key, reason: "fal_register" },
    });
  };

  const accept = async () => {
    // Nothing to burn in mocks, but the button still has to move the flow on.
    if (mockScreen) {
      setScreen("SUCCESS");
      return;
    }

    if (!fromParams && !token) return;

    // Terms on an opening link, the conformity on a linking one.
    const documentVersion =
      (variant === "VINCULACION" && flow.linkingDocumentVersion) ||
      flow.openingDocumentVersion;

    setIsSubmitting(true);
    try {
      await (fromParams
        ? acceptAccountRequestNotification({
            uuid: requestUuid,
            token: requestToken,
          })
        : acceptConsentLink({
            baseUrl: flow.baseUrl,
            token: token as string,
            documentVersion,
            accountNumber:
              variant === "VINCULACION" ? selectedComitente : undefined,
          }));
      if (fromParams) await registerRequest();
      setScreen("SUCCESS");
    } catch (error) {
      // Accepted before the sheet existed, or the first register never made it.
      if (fromParams && reasonFromError(error) === "already_used")
        await registerRequest();
      // 409 already signed, 410 expired mid-flow: same mapping as validate.
      failWith(error, "accept", variant);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    screen,
    variant,
    cuit,
    comitentes,
    selectedComitente,
    selectComitente: setSelectedComitente,
    /** Empty whenever the name lookup came back without one. */
    name: toDisplayCase(firstName),
    lastName: toDisplayCase(lastName),
    // Not title-cased: a razón social would turn "S.A." into "S.a.".
    holder,
    comitente,
    accepted,
    isSubmitting,
    toggleAccepted: (name: string) =>
      setAccepted((current) => ({ ...current, [name]: !current[name] })),
    /**
     * Opening signs here: its documents open as PDFs from the checkbox links,
     * so nothing is left to walk through. Linking signs after the data check.
     */
    submitWelcome: () =>
      variant === "VINCULACION" ? setScreen("PRECONFIRMATION") : accept(),
    confirmPreconfirmation: accept,
    backToWelcome: () => setScreen("WELCOME"),
    /** The generic error's only action: start over from validation. */
    retry: validate,
  };
};

export default usePartnerConsent;
